import type { IconButtonProps } from '@mui/material/IconButton';

import { useState, useCallback } from 'react';
import { usePopover } from 'minimal-shared/hooks';
import { m, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMegaphone, faChevronLeft } from '@fortawesome/pro-regular-svg-icons';

import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import MenuList from '@mui/material/MenuList';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

import { useTranslate } from 'src/locales';
import { markAppUpdateAsSeen, useGetRecentAppUpdates } from 'src/actions/app-updates/app-updates';

import { Scrollbar } from 'src/components/scrollbar';
import { CustomPopover } from 'src/components/custom-popover';
import { varTap, varHover, transitionTap } from 'src/components/animate';

import { AppUpdateItem } from './app-update-item';
import { AppUpdateDetailView } from './app-update-detail-view';

const POPOVER_WIDTH = 400;
const TRANSITION_DURATION = 0.25;

const slideVariants = {
  list: {
    initial: { x: '-100%', opacity: 0 },
    animate: { x: 0, opacity: 1, transition: { duration: TRANSITION_DURATION, ease: 'easeOut' } },
    exit: { x: '-100%', opacity: 0, transition: { duration: TRANSITION_DURATION, ease: 'easeIn' } },
  },
  detail: {
    initial: { x: '100%', opacity: 0 },
    animate: { x: 0, opacity: 1, transition: { duration: TRANSITION_DURATION, ease: 'easeOut' } },
    exit: { x: '100%', opacity: 0, transition: { duration: TRANSITION_DURATION, ease: 'easeIn' } },
  },
};

export type AppUpdatesPopoverProps = IconButtonProps;

export function AppUpdatesPopover({ sx, ...other }: AppUpdatesPopoverProps) {
  const { t } = useTranslate('app-updates');
  const { open, anchorEl, onClose, onOpen } = usePopover();
  const [selectedUpdateId, setSelectedUpdateId] = useState<number | null>(null);

  const {
    updates,
    updatesLoading,
    updatesError,
    updatesEmpty,
    updatesRefresh,
    unreadCount,
    hasUnread,
  } = useGetRecentAppUpdates(5);

  const selectedUpdate = selectedUpdateId
    ? (updates.find((u) => u.id === selectedUpdateId) ?? null)
    : null;

  const handlePopoverClose = useCallback(() => {
    onClose();
    setSelectedUpdateId(null);
  }, [onClose]);

  const handleItemClick = useCallback(
    async (id: number) => {
      setSelectedUpdateId(id);

      try {
        await markAppUpdateAsSeen(id);
        updatesRefresh();
      } catch (error) {
        console.error('Failed to mark app update as seen:', error);
      }
    },
    [updatesRefresh]
  );

  const handleBack = useCallback(() => {
    setSelectedUpdateId(null);
    updatesRefresh();
  }, [updatesRefresh]);

  const renderListContent = () => {
    if (updatesLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, px: 0, mx: 0 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (updatesError) {
      return (
        <Typography sx={{ p: 2, color: 'error.main', textAlign: 'center' }}>
          {t('errors.load')}
        </Typography>
      );
    }

    if (updatesEmpty) {
      return (
        <Typography sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
          {t('empty')}
        </Typography>
      );
    }

    return (
      <MenuList>
        {updates.map((update) => (
          <AppUpdateItem key={update.id} update={update} onClick={handleItemClick} />
        ))}
      </MenuList>
    );
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap={varTap(0.96)}
        whileHover={varHover(1.04)}
        transition={transitionTap()}
        aria-label={t('ariaLabel')}
        onClick={onOpen}
        sx={[
          (theme) => ({
            ...(open && { bgcolor: theme.vars.palette.action.selected }),
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...other}
      >
        <Badge badgeContent={unreadCount} color="error" invisible={!hasUnread}>
          <FontAwesomeIcon icon={faMegaphone} style={{ width: 24, height: 24 }} />
        </Badge>
      </IconButton>

      <CustomPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        slotProps={{
          paper: { sx: { width: POPOVER_WIDTH, overflow: 'hidden' } },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.vars.palette.divider}`,
          }}
        >
          {selectedUpdate ? (
            <IconButton onClick={handleBack} size="small">
              <FontAwesomeIcon icon={faChevronLeft} style={{ width: 18, height: 18 }} />
            </IconButton>
          ) : (
            <Box sx={{ width: 28, height: 34 }} />
          )}
          <Typography variant="subtitle1" sx={{ flex: 1, textAlign: 'center' }}>
            {t('title')}
          </Typography>
          <Box sx={{ width: 34, height: 34 }} />
        </Box>

        <Box sx={{ position: 'relative', height: 400 }}>
          <AnimatePresence mode="wait" initial={false}>
            {selectedUpdate ? (
              <m.div
                key="detail"
                variants={slideVariants.detail}
                initial="initial"
                animate="animate"
                exit="exit"
                style={{ height: '100%' }}
              >
                <Scrollbar sx={{ height: '100%' }}>
                  <Box sx={{ p: 1.5 }}>
                    <AppUpdateDetailView update={selectedUpdate} />
                  </Box>
                </Scrollbar>
              </m.div>
            ) : (
              <m.div
                key="list"
                variants={slideVariants.list}
                initial="initial"
                animate="animate"
                exit="exit"
                style={{ height: '100%' }}
              >
                <Scrollbar sx={{ height: '100%' }}>{renderListContent()}</Scrollbar>
              </m.div>
            )}
          </AnimatePresence>
        </Box>
      </CustomPopover>
    </>
  );
}
